// Turns the progress models into EN / MS text, from the portal dictionary.
// Every sentence a progress view (screen or PDF) can show is produced here or
// is a dictionary value, so the banned-word guard can enumerate them all.

import type { Assignment, IsoDate, StepStage } from "../domain/types";
import { fill, type Locale, type PortalDictionary } from "../i18n/dictionary";
import type { OnePagerModel, StepRef, SupportKey } from "./onePager";
import { parseIsoDate } from "./weeks";

type P = PortalDictionary["service"]["progress"];

export function stageLabel(p: P, stage: StepStage | null): string {
  switch (stage) {
    case "exploring":
      return p.stageExploring;
    case "help":
      return p.stageHelp;
    case "own":
      return p.stageOwn;
    case "steady":
      return p.stageSteady;
    case "places":
      return p.stagePlaces;
    default:
      return p.stageNone;
  }
}

const BED_KEY: Record<string, keyof P> = {
  language: "bedLanguage",
  literacy: "bedLiteracy",
  numeracy: "bedNumeracy",
  executive_function: "bedExecutiveFunction",
  social_emotional: "bedSocialEmotional",
  daily_life: "bedDailyLife",
  motor: "bedMotor",
  perception: "bedPerception",
  creativity: "bedCreativity",
  attention: "bedAttention",
};

/** The shared area (Skill Garden bed) name — the same words the family's app uses. */
export function bedLabel(p: P, domain: string): string {
  const key = BED_KEY[domain];
  return key ? (p[key] as string) : p.bedOther;
}

const SUPPORT_KEY: Record<SupportKey, keyof P> = {
  captions: "captions",
  reducedMotion: "reducedMotion",
  highContrast: "highContrast",
  calmMode: "calmMode",
  tapOnly: "tapOnly",
};

export function languageName(p: P, code: string): string {
  const c = code.toLowerCase();
  if (c.startsWith("en")) return p.langEn;
  if (c.startsWith("ms")) return p.langMs;
  return code.toUpperCase();
}

/** "How best to support me" lines. */
export function supportLines(p: P, model: OnePagerModel): string[] {
  const { support } = model;
  if (!support.shared) return [p.supportNone];
  const lines = support.settings.map((k) => p[SUPPORT_KEY[k]] as string);
  if (support.language) lines.push(fill(p.language, { language: languageName(p, support.language) }));
  return lines.length > 0 ? lines : [p.supportDefaults];
}

/** "Practice at home this period" lines. */
export function practiceLines(p: P, model: OnePagerModel): string[] {
  const pr = model.practice;
  const days =
    pr.daysPlayed === 0
      ? p.daysPlayedNone
      : pr.daysPlayed === 1
        ? p.daysPlayedOne
        : fill(p.daysPlayed, { n: pr.daysPlayed });
  const lines = [days];
  if (pr.daysPlayed > 0) lines.push(fill(p.activeWeeks, { n: pr.activeWeeks, total: pr.weeksInPeriod }));
  if (model.hasSteps) {
    lines.push(fill(p.stepsMovedUp, { n: pr.stepsMovedUp }));
    lines.push(fill(p.stepsInProgress, { n: pr.stepsInProgress }));
  }
  return lines;
}

export function localeTag(locale: Locale): string {
  return locale === "ms" ? "ms-MY" : "en-GB";
}

/** A calendar date ("YYYY-MM-DD", read as UTC) for display, e.g. "6 Oct 2026". */
export function formatDay(locale: Locale, iso: IsoDate, withYear = true): string {
  return parseIsoDate(iso).toLocaleDateString(localeTag(locale), {
    day: "numeric",
    month: "short",
    ...(withYear ? { year: "numeric" } : {}),
    timeZone: "UTC",
  });
}

/** An instant for display in the viewer's own time zone. */
export function formatInstant(locale: Locale, iso: string): string {
  return new Date(iso).toLocaleDateString(localeTag(locale), { day: "numeric", month: "short", year: "numeric" });
}

export function stepLine(p: P, step: StepRef): string {
  return fill(p.stepLine, { skill: step.skillLabel, n: step.step });
}

export function lastPractisedLine(p: P, locale: Locale, d: IsoDate | null): string {
  return d ? fill(p.lastPractised, { date: formatDay(locale, d) }) : p.notPractised;
}

/** A centre suggestion's status, in plain words. */
export function assignmentStatus(p: P, locale: Locale, a: Pick<Assignment, "status" | "seenAt">): string {
  if (a.status === "explored") return p.statusPlayed;
  if (a.seenAt) return fill(p.statusSeen, { date: formatInstant(locale, a.seenAt) });
  if (a.status === "in-progress") return p.statusOpened;
  return p.statusAssigned;
}
